import { SettingCompanyViewModel } from 'src/app/modules/admin/setting-management/setting-company/setting-company';
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { SettingService } from "./setting.service";
import { Setting } from "../models/setting.model";
import { ReturnResult } from "../models/return-result";
import { decodeSettingCacheMetadata, SettingCache } from '../utility/settingCacheDecode';

@Injectable({
  providedIn: 'root'
})
export class SettingPoolService {
  private readonly MAX_VERSION = 65535;
  private readonly MAX_AGE = 10;
  private readonly CACHE_KEY = 'allSetting';
  private readonly VERSION_KEY = 'sync_version';
  private syncPromise: Promise<void> | null = null;

  constructor(private settingService: SettingService) {
  }

  async getAllSettingAllow(callbackFunction = async () => {}) {
    let isSettingExist = window.localStorage.getItem('allSetting');
    if (isSettingExist) {
      await callbackFunction();
    }
    else {
      try{
        var settingAllow = await this.settingService.getAllSettingAllow().toPromise();
        if (settingAllow && settingAllow.result) {
          let returnData = settingAllow.result;
          returnData = returnData.map(setting => {
            return {
              ...setting,
              settingLevel: undefined,
            }
          })
          window.localStorage.setItem('allSetting', JSON.stringify(returnData));

          await callbackFunction();
        }
        else {
          console.log("Fetching all Setting Fail...");
        }
      }
      catch (error){
        console.log(error);
      }
    }
  }

  async getConfigCompany() {
    let dataReturn: SettingCompanyViewModel = new SettingCompanyViewModel();
    try {
      this.getAllSettingAllow(async () => {
        let listSettingAllowed = window.localStorage.getItem('allSetting');
        let listSettings = JSON.parse(listSettingAllowed) as Setting[];
        listSettings = listSettings?.filter(setting => (setting.group === 'COMPANY'))

        if (listSettings) {
          dataReturn.companyName = this.getSettingValueClientSide(listSettings, 'COMPANY_NAME');
          dataReturn.themeColor = this.getSettingValueClientSide(listSettings, 'THEME_COLOR');
          dataReturn.urlCompanyBackGround = this.getSettingValueClientSide(listSettings, 'COMPANY_BACKGROUND');
          dataReturn.urlCompanyLogo = this.getSettingValueClientSide(listSettings, 'COMPANY_LOGO');
          dataReturn.urlCompanyLogoPortal = this.getSettingValueClientSide(listSettings, 'COMPANY_LOGO_PORTAL');
          dataReturn.urlDefaultAvatar = this.getSettingValueClientSide(listSettings, "DEFAULT_AVATAR"),
          dataReturn.urlCompanyFavicon = this.getSettingValueClientSide(listSettings, "COMPANY_FAVICON")

          dataReturn.urlCompanyBackGround = (dataReturn.urlCompanyBackGround != null) ? dataReturn.urlCompanyBackGround?.replace(/\\/g, '/') : null;
          dataReturn.urlCompanyLogoPortal = (dataReturn.urlCompanyLogoPortal != null) ? dataReturn.urlCompanyLogoPortal?.replace(/\\/g, '/') : null;
          dataReturn.urlCompanyLogo = (dataReturn.urlCompanyLogo != null) ? dataReturn.urlCompanyLogo?.replace(/\\/g, '/') : null;
          dataReturn.urlDefaultAvatar = (dataReturn.urlDefaultAvatar != null) ? dataReturn.urlDefaultAvatar?.replace(/\\/g, '/') : null;
        }
        else {
          try{
            dataReturn = await this.settingService.getConfigCompany().toPromise();
          }
          catch (error){
            console.log("Fetch config company setting failed");
          }
        }
      })
    }
    catch (error) {
      console.log(error)
      console.log("Get ConfigCompany");

      dataReturn = await this.settingService.getConfigCompany().toPromise();
    }

    return of(dataReturn);
  }

  getSettingByKeyAndGroup(key: string, group: string): Observable<ReturnResult<Setting>> {
    return new Observable<ReturnResult<Setting>>(observer => {
      this.getAllSettingAllow(async () => {
        try {
          const listSettingAllowed = window.localStorage.getItem('allSetting');
          const listSettings = JSON.parse(listSettingAllowed || '[]') as Setting[];
          const matchedSetting = listSettings?.find(setting => setting.group === group && setting.key === key);

          if (matchedSetting) {
            const returnResult: ReturnResult<Setting> = new ReturnResult<Setting>();
            returnResult.result = matchedSetting;
            observer.next(returnResult);
            observer.complete();
            return;
          }

          this.settingService.getSettingByKeyAndGroup(key, group).subscribe({
            next: (setting) => observer.next(setting),
            error: (err) => observer.error(err),
            complete: () => observer.complete(),
          });
        }
        catch (error) {
          console.log(error);
          console.log("Query: Key ", key, " and Group ", group);
          this.settingService.getSettingByKeyAndGroup(key, group).subscribe({
            next: (setting) => observer.next(setting),
            error: (err) => observer.error(err),
            complete: () => observer.complete(),
          });
        }
      }).catch((error) => {
        console.log(error);
        console.log("Query: Key ", key, " and Group ", group);
        observer.error(error);
      });
    });
  }

  getSettingByGroupName(group: string){
    let dataReturn;
    try {
      this.getAllSettingAllow(async () => {
        let listSettingAllowed = window.localStorage.getItem('allSetting');
        let listSettings = JSON.parse(listSettingAllowed) as Setting[];
        listSettings = listSettings?.filter(setting => (setting.group === group));
        if (listSettings) {
          let returnResult : ReturnResult<Setting[]> = new ReturnResult<Setting[]>();
          returnResult.result = listSettings;
          dataReturn = of(returnResult);
        }
        else {
          dataReturn = this.settingService.getSettingByGroupName(group).subscribe(setting => {
            return of((setting) ? setting : null)
          })
        }
      });
    }
    catch (error) {
      console.log(error)
      console.log("Query: Group ", group);

      dataReturn = this.settingService.getSettingByGroupName(group).subscribe(setting => {
        return of((setting && setting.result) ? setting.result : null)
      })
    }

    return dataReturn;
  }

  private getSettingValueClientSide(settingList: Setting[], key: string) {
    let setting = settingList.find(setting => setting.key === key);
    return (setting) ? setting.value : null;
  }

  /**
   * Orchestrates the sync process. Uses a Promise lock to prevent multiple
   * concurrent requests when components load simultaneously.
   */
  public async syncSettingsIfNeeded(metadata: string = null): Promise<void> {
    // If a sync is already in progress, wait for it to finish
    if (this.syncPromise) {
      return this.syncPromise;
    }

    this.syncPromise = this.executeSyncWorkflow(metadata).finally(() => {
      this.syncPromise = null;
    });
    return this.syncPromise;
  }

  /**
   * The main workflow for checking metadata and deciding whether to perform
   * a Delta Sync, a Full Sync, or do nothing.
   */
  private async executeSyncWorkflow(metadata: string = null): Promise<void> {
    try {
      let isSynced = false;
      const localVersion = this.getLocalVersion();

      // 1. Fetch metadata from the server (Expected Base64 string)
      let meta: SettingCache = null;
      if (metadata) {
        const decodedMeta = decodeSettingCacheMetadata(metadata);
        if (decodedMeta && decodedMeta.version !== undefined) {
          meta = decodedMeta;
        }
      } else {
        const metaRes = await this.settingService.getSyncMetadata().toPromise();
        const decodedMeta = decodeSettingCacheMetadata(metaRes?.result);
        if (decodedMeta && decodedMeta.version !== undefined) {
          meta = decodedMeta;
        }
      }
      if (!meta || meta.version === undefined) return;

      // 2. Calculate version distance
      const distance = (meta.version - localVersion + this.MAX_VERSION) % this.MAX_VERSION;
      if (distance === 0) return; // Cache is up to date

      if (localVersion !== -1 && distance <= this.MAX_AGE) {
        // Within the sliding window: Perform Delta Sync
        isSynced = await this.executeDeltaSync(meta);
      } else {
        // Exceeded sliding window or fresh install: Invalidate cache for Full Sync
        this.triggerFullSyncFallback();
      }

      // 4. Update the local version marker
      if (localVersion === -1 || distance > this.MAX_AGE || isSynced) {
        this.setLocalVersion(meta.version);
      }
    } catch (error) {
      console.error("Sync Setting Error:", error);
    }
  }

  // =========================================================================
  // SYNCHRONIZATION WORKERS (DATA MANIPULATION)
  // =========================================================================

  /**
   * Fetches only the changed keys and merges them into the existing cache.
   */
  private async executeDeltaSync(meta: SettingCache): Promise<boolean> {
    // Extract the string keys from the decoded metadata array
    const keysToFetch = meta.changedKeys ? meta.changedKeys.map(k => k.key) : [];
    if (keysToFetch.length === 0) return false;

    // Fetch the actual setting values from the API
    const res = await this.settingService.getDeltaSettings(keysToFetch).toPromise();
    if (!res || !res.result) return false;

    // Retrieve current cache
    let cache: Setting[] = JSON.parse(window.localStorage.getItem(this.CACHE_KEY) || '[]');

    // Apply updates
    cache = this.applyWildcardCleanup(cache, keysToFetch);
    cache = this.mergeNewSettings(cache, res.result);

    // Save back to storage
    window.localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    return true;
  }

  /**
   * Removes cached items based on wildcard commands (e.g., 'SYSTEM:*' or '*:THEME').
   */
  private applyWildcardCleanup(cache: Setting[], keysToFetch: string[]): Setting[] {
    let updatedCache = [...cache];

    keysToFetch.forEach(compositeKey => {
      const parts = compositeKey.split(':');
      if (parts.length < 2) return;

      const [group, key] = parts;

      if (group === '*') {
        // Legacy support: remove specific key across all groups
        updatedCache = updatedCache.filter(s => s.key !== key);
      } else if (key === '*') {
        // Bulk update support: remove all keys in a specific group
        updatedCache = updatedCache.filter(s => s.group !== group);
      }
    });

    return updatedCache;
  }

  /**
   * Merges newly fetched settings into the cache, overwriting existing ones or appending new ones.
   */
  private mergeNewSettings(cache: Setting[], newSettings: Setting[]): Setting[] {
    const updatedCache = [...cache];

    newSettings.forEach((newSetting: Setting) => {
      const index = updatedCache.findIndex(s => s.group === newSetting.group && s.key === newSetting.key);
      const cleanedSetting = { ...newSetting, settingLevel: undefined };

      if (index !== -1) {
        updatedCache[index] = cleanedSetting; // Update existing
      } else {
        updatedCache.push(cleanedSetting);    // Insert new
      }
    });

    return updatedCache;
  }

  /**
   * Forces the legacy flow to re-fetch all settings by deleting the cache.
   */
  private triggerFullSyncFallback(): void {
    window.localStorage.removeItem(this.CACHE_KEY);
  }

  private getLocalVersion(): number {
    const localVersionStr = window.localStorage.getItem(this.VERSION_KEY);
    return localVersionStr ? parseInt(localVersionStr, 10) : -1;
  }

  private setLocalVersion(version: number): void {
    window.localStorage.setItem(this.VERSION_KEY, version.toString());
  }

  /**
   * Expose current cached version for external consumers (e.g. ProcessingStatusService listeners)
   * so they can cheaply compare incoming versions before triggering a full sync workflow.
   */
  public getCurrentVersion(): number {
    return this.getLocalVersion();
  }
}
